import { useMemo } from 'react'
import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { getConversationThread } from '@/services/conversationService'
import type { ConversationThreadPage, ConversationThreadEntry } from '@/types/Conversation'

export function threadQueryKey(contactPhone: string | null) {
  return ['conversation-thread', contactPhone] as const
}

export function useConversationThread(contactPhone: string | null) {
  const query = useInfiniteQuery({
    queryKey: threadQueryKey(contactPhone),
    queryFn: ({ pageParam }) => getConversationThread(contactPhone as string, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: contactPhone !== null,
  })

  // Pages arrive newest-first (page 0 = most recent 20); each page's own
  // entries are already oldest-to-newest. Reverse the page order so the
  // flattened list reads oldest-to-newest overall, top to bottom — the
  // same convention Phase 1's per-session hook used.
  const entries = useMemo(() => {
    if (!query.data) return []
    return [...query.data.pages].reverse().flatMap((page) => page.entries)
  }, [query.data])

  return { ...query, entries }
}

export function appendLiveThreadEntry(
  queryClient: ReturnType<typeof useQueryClient>,
  contactPhone: string,
  entry: ConversationThreadEntry,
): void {
  queryClient.setQueryData<InfiniteData<ConversationThreadPage>>(
    threadQueryKey(contactPhone),
    (current) => {
      if (!current || current.pages.length === 0) return current

      const pages = [...current.pages]
      pages[0] = { ...pages[0], entries: [...pages[0].entries, entry] }
      return { ...current, pages }
    },
  )
}

/** A brand new session for a contact means the currently-open thread (if
 * any) needs its cached pages thrown away — the freshest page fetched next
 * won't line up with a `nextCursor` computed against the old session set. */
export function invalidateThread(
  queryClient: ReturnType<typeof useQueryClient>,
  contactPhone: string,
): void {
  void queryClient.invalidateQueries({ queryKey: threadQueryKey(contactPhone) })
}
